import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Json } from '@/types/database.generated';
import type { TreatmentCourseTemplateDraft } from '@/types/treatmentCourses';
import {
  buildTreatmentCourseTemplatePayload,
  parseTreatmentCourseTemplate,
  parseTreatmentCourseTemplateList,
} from '@/lib/treatmentCourseTemplatePayload';

export const treatmentCourseTemplatesQueryKey = (
  clinicId: string | null,
  includeInactive: boolean,
) => ['treatment-course-templates', clinicId, { includeInactive }] as const;

export function useTreatmentCourseTemplates(
  clinicId: string | null | undefined,
  options?: { includeInactive?: boolean },
) {
  const includeInactive = options?.includeInactive ?? true;
  return useQuery({
    queryKey: treatmentCourseTemplatesQueryKey(clinicId ?? null, includeInactive),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<TreatmentCourseTemplateDraft[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase.rpc('list_treatment_course_templates', {
        p_clinic_id: clinicId,
        p_include_inactive: includeInactive,
      });
      if (error) throw error;
      return parseTreatmentCourseTemplateList(data);
    },
  });
}

export function useTreatmentCourseTemplate(
  clinicId: string | null | undefined,
  templateId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['treatment-course-template', clinicId, templateId] as const,
    enabled: Boolean(clinicId && templateId),
    queryFn: async (): Promise<TreatmentCourseTemplateDraft | null> => {
      if (!clinicId || !templateId) return null;
      const { data, error } = await supabase.rpc('get_treatment_course_template', {
        p_clinic_id: clinicId,
        p_template_id: templateId,
      });
      if (error) throw error;
      return parseTreatmentCourseTemplate(data);
    },
  });
}

export function useReplaceTreatmentCourseTemplate(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (draft: TreatmentCourseTemplateDraft) => {
      if (!clinicId) throw new Error('clinic_id is required');
      const payload = buildTreatmentCourseTemplatePayload(draft);
      const { data, error } = await supabase.rpc('replace_treatment_course_template', {
        p_clinic_id: clinicId,
        p_template: payload as unknown as Json,
      });
      if (error) throw error;
      const parsed = parseTreatmentCourseTemplate(data);
      if (!parsed) throw new Error('replace_treatment_course_template returned no template');
      return parsed;
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({
        queryKey: ['treatment-course-templates', clinicId ?? null],
      });
      void queryClient.invalidateQueries({
        queryKey: ['treatment-course-template-by-service', clinicId ?? null],
      });
      if (saved.id) {
        queryClient.setQueryData(['treatment-course-template', clinicId ?? null, saved.id], saved);
      }
    },
  });
}

export function useTreatmentCourseTemplateByCatalogService(
  clinicId: string | null | undefined,
  serviceId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['treatment-course-template-by-service', clinicId, serviceId] as const,
    enabled: Boolean(clinicId && serviceId),
    queryFn: async (): Promise<TreatmentCourseTemplateDraft | null> => {
      if (!clinicId || !serviceId) return null;
      const { data: row, error } = await supabase
        .from('treatment_course_templates')
        .select('id')
        .eq('clinic_id', clinicId)
        .eq('catalog_service_id', serviceId)
        .maybeSingle();
      if (error) throw error;
      if (!row?.id) return null;
      const { data, error: loadError } = await supabase.rpc('get_treatment_course_template', {
        p_clinic_id: clinicId,
        p_template_id: row.id,
      });
      if (loadError) throw loadError;
      const parsed = parseTreatmentCourseTemplate(data);
      return parsed ? { ...parsed, catalog_service_id: serviceId } : null;
    },
  });
}

export async function linkTreatmentCourseTemplateCatalogService(
  clinicId: string,
  templateId: string,
  serviceId: string,
): Promise<void> {
  const { error } = await supabase
    .from('treatment_course_templates')
    .update({ catalog_service_id: serviceId })
    .eq('id', templateId)
    .eq('clinic_id', clinicId);
  if (error) throw error;
}

export function useDeleteTreatmentCourseTemplate(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string) => {
      if (!clinicId) throw new Error('clinic_id is required');
      const { error } = await supabase
        .from('treatment_course_templates')
        .delete()
        .eq('id', templateId)
        .eq('clinic_id', clinicId);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['treatment-course-templates', clinicId ?? null],
      });
      void queryClient.invalidateQueries({
        queryKey: ['treatment-course-template-by-service', clinicId ?? null],
      });
    },
  });
}
